import { Table, Typography, message, Row, Card, Button } from "antd"
import { useEffect, useState } from "react";
import { trainingAPI } from "@api/TrainingAPI"
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PartitionOutlined } from "@ant-design/icons";
import ThirdPartyIntegrationModal from "./ThirdPartyIntegrationModal/ThirdPartyIntegrationModal";
import { PermissionedButton, PermissionedPopConfirm, useUserPermissions } from "@components/PermissionedComponents";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { AdminNotice } from "./AdminNotice";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import ExternalDataConnections from "./external-data-connections.png";
import { getTeamInfo } from "@store/actions/team.actions";

const { Title, Text } = Typography;

export const ThirdPartyIntegration = () => {
    const dispatch = useDispatch();

    const { user } = useAuth0();
    const {
        info: { 
            third_party_integration_management_enabled,
            permissions_manager_list
         }
    } = useSelector(({ team }) => team);
    const userPermissions = useUserPermissions();

    const isStaff = isStaffEmail(user?.email);

    const [thirdPartyOnboardingData, setThirdPartyOnboardingData] = useState<IThirdParthOnboardingData[] | undefined>(undefined)
    const [showThirdPartyIntegrationModal, setShowThirdPartyIntegrationModal] = useState<boolean>(false);
    const [service, setService] = useState<string | undefined>();
    const [thirdPartyIntegrationModalData, setThirdPartyIntegrationModalData] = useState<object>({})
    const [modalStatus, setModalStatus] = useState<"add" | "edit">("add")
    const hasManageThirdPartyIntegrationPermission = userPermissions?.ManageExternalDataConnections || false;

    const refetchTableData = async () => {
        if ((third_party_integration_management_enabled && hasManageThirdPartyIntegrationPermission ) || isStaff) {
            setThirdPartyOnboardingData(await trainingAPI.getThirdPartyOnboarding())
        }
    }

    useEffect(() => {
        (async () => {
            await refetchTableData()
        })()
    }, [third_party_integration_management_enabled, hasManageThirdPartyIntegrationPermission, isStaff])

    const columns: ColumnsType<IThirdParthOnboardingData> = [
        {
            title: <div style={{ textAlign: 'left' }}>Service</div>,
            dataIndex: "name",
            key: "name",
            render: (text) => (
                <div style={{ textAlign: 'left' }}>
                    <PartitionOutlined style={{ marginRight: 8 }} /> <Text>{text}</Text>
                </div>
            )
        },
        {
            title: "Added by",
            dataIndex: "added_by",
            key: "added_by",
            render: (text) => <Text>{text || "Admin"}</Text>
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, record) => (
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }}>
                    <PermissionedButton
                        shape="circle"
                        icon={<EditOutlined />}
                        permissionCheck="ManageExternalDataConnections"
                        tooltipMessage="You don't have permission to edit third party integration"
                        type="primary"
                        ghost
                        disabled={record.edit_allowed === false}
                        onClick={() => {
                            setService(record.service);
                            setThirdPartyIntegrationModalData({})
                            setModalStatus("edit")
                            setShowThirdPartyIntegrationModal(true)
                        }}
                    />

                    <PermissionedPopConfirm
                        permissionCheck="ManageExternalDataConnections"
                        title="Are you sure to delete?"
                        onConfirm={async () => {
                            try {
                                await trainingAPI.deleteThirdPartyOnboarding(record.service)
                                await refetchTableData();
                                dispatch(getTeamInfo());

                                message.success("Service deleted")
                            } catch (err) {
                                message.error((err as any).response.data)
                            }
                        }}
                        okText="Yes"
                        cancelText="No"
                        disabled={record.edit_allowed === false}
                    >
                        <Button icon={<DeleteOutlined />} shape="circle" danger ghost disabled={record.edit_allowed === false} />
                    </PermissionedPopConfirm>
                </div>
            ),
        }
    ]

    if (!third_party_integration_management_enabled && !isStaff) {
        return (
            <PremiumFeatureDisabled
                bg_image={ExternalDataConnections}
                feature_name={"External Data Connections"}
                show_navbar={false}
                info_text={<>
                    This is an elite feature that will allow you to connect and import data from external sources. <br />
                    If you are interested in adding this to your platform, please contact your sales rep!</>}
            />
        )
    }

    if (third_party_integration_management_enabled && !hasManageThirdPartyIntegrationPermission) {
        return (
        <div>
            You do not have permissions to view this page. <br/> 
            If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br/><br/>
            Your team's Permissions Manager(s): <strong>{permissions_manager_list.map((user: { first_name: any; last_name: any; }) => `${user.first_name} ${user.last_name}`).join(", ")}</strong>
        </div>
        )
    }

    return (
        <>
            {(!third_party_integration_management_enabled && isStaff) && (
                <div style={{ margin: '16px', maxWidth: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
                    <AdminNotice description={undefined} />
                </div>
            )}

            {showThirdPartyIntegrationModal &&
                <ThirdPartyIntegrationModal
                    visibility={showThirdPartyIntegrationModal}
                    setVisibility={setShowThirdPartyIntegrationModal}
                    service={service}
                    setService={setService}
                    thirdPartyIntegrationModalData={thirdPartyIntegrationModalData}
                    setThirdPartyIntegrationModalData={setThirdPartyIntegrationModalData}
                    modalStatus={modalStatus}
                    refetchTableData={refetchTableData}
                    servicesWithKey={thirdPartyOnboardingData?.map((e) => e.service)}
                />
            }

            <Card style={{ marginBottom: "20px" }}>
                <Title level={4}>
                    Setup a New Data Connection
                </Title>

                <Text type="secondary">Select an external data source, provide some details, and start importing your data into Whistle Performance</Text>

                <Row style={{ marginTop: "15px", marginBottom: "10px" }}>
                    <PermissionedButton
                        permissionCheck="ManageExternalDataConnections"
                        tooltipMessage="You don't have permission to add third party integration"
                        type="primary"
                        onClick={() => {
                            setService(undefined);
                            setThirdPartyIntegrationModalData({})
                            setModalStatus("add")
                            setShowThirdPartyIntegrationModal(true)
                        }}
                    >
                        Get Started
                    </PermissionedButton>
                </Row>
            </Card>


            <Card>
                <Title level={4}>
                    Your data connections
                </Title>

                <Text type="secondary">Manage your currently connected external data sources. Credentials are not displayed here.</Text>
                <Table size="small" columns={columns} dataSource={thirdPartyOnboardingData} pagination={false} style={{ marginTop: '10px' }} />
            </Card>
        </>
    )
}

interface IThirdParthOnboardingData {
    service: string;
    name: string;
    added_by: string;
    edit_allowed: boolean;
}

export default ThirdPartyIntegration